import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { GOLD_BADGE_BG, GOLD_BASE, GOLD_TEXT, rankedBarGradient } from '../data/marketColors';
import type { MarketRow } from '../data/marketPerformanceData';
import {
  barFillPercent,
  formatMarketMillions,
  formatMarketRate,
  formatMarketThousands,
} from '../data/utils';

const GRID_COLUMNS = '20px 250px 1fr 80px 80px';

function ColumnHeader({
  label,
  align = 'left',
}: {
  label: string;
  align?: 'left' | 'right';
}): ReactNode {
  return (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        color: 'text.secondary',
        textAlign: align,
      }}
    >
      {label}
    </Typography>
  );
}

function MarketBarRow({
  market,
  index,
  count,
  leader,
  maxPerformanceIndex,
  currencySymbol,
}: {
  market: MarketRow;
  index: number;
  count: number;
  leader: boolean;
  maxPerformanceIndex: number;
  currencySymbol: string;
}): ReactNode {
  const palette = rankedBarGradient(index, count, leader);
  const barWidth = barFillPercent(market.performanceIndex, maxPerformanceIndex);
  const glow = leader
    ? `0 2px 8px ${alpha(GOLD_BASE, 0.3)}`
    : `0 2px 6px ${alpha(palette[1], 0.2)}`;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        gap: 2,
        alignItems: 'center',
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          textAlign: 'center',
          color: leader ? GOLD_TEXT : 'text.disabled',
        }}
      >
        {index + 1}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            fontSize: 9,
            fontWeight: 700,
            color: 'common.white',
            borderRadius: 1,
            py: 0.5,
            px: 1,
            background: `linear-gradient(135deg, ${palette[0]}, ${palette[1]})`,
          }}
        >
          {market.flag}
        </Box>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            fontWeight: leader ? 700 : 500,
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {market.market}
        </Typography>
        {leader && (
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: GOLD_TEXT,
              bgcolor: GOLD_BADGE_BG,
              borderRadius: 1,
              py: 0.5,
              px: 1,
            }}
          >
            Most efficient
          </Box>
        )}
      </Box>

      <Box sx={{ position: 'relative', height: 20 }}>
        {[0.25, 0.5, 0.75].map((tick) => (
          <Box
            key={tick}
            sx={{
              position: 'absolute',
              left: `${tick * 100}%`,
              top: 0,
              bottom: 0,
              width: '1px',
              bgcolor: 'divider',
            }}
          />
        ))}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'background.page',
            borderRadius: 2,
          }}
        />
        <Box
          data-test="market-bar-fill"
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${barWidth}%`,
            borderRadius: 2,
            boxShadow: glow,
            background: `linear-gradient(90deg, ${palette[0]}, ${palette[1]})`,
          }}
        />
      </Box>

      <Box sx={{ textAlign: 'right' }}>
        <Typography
          sx={{
            display: 'block',
            fontSize: 12,
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          {formatMarketRate(market.savingsRate)}
        </Typography>
        <Typography sx={{ display: 'block', fontSize: 10, color: 'text.secondary' }}>
          {formatMarketThousands(market.potentialSavingsK, currencySymbol)}
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary' }}>
          {formatMarketMillions(market.spendMillions, currencySymbol)}
        </Typography>
      </Box>
    </Box>
  );
}

export function MarketBars({
  markets,
  currencySymbol,
  rankingMarkets = markets,
  rankOffset = 0,
}: {
  markets: MarketRow[];
  currencySymbol: string;
  rankingMarkets?: MarketRow[];
  rankOffset?: number;
}): ReactNode {
  const maxPerformanceIndex = Math.max(...rankingMarkets.map((market) => market.performanceIndex));

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID_COLUMNS,
          gap: 2,
          pb: 2,
          mb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <span />
        <ColumnHeader label="Market" />
        <ColumnHeader label="Performance index" />
        <ColumnHeader label="Potential savings" align="right" />
        <ColumnHeader label="Total spend" align="right" />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {markets.map((market, index) => (
          <MarketBarRow
            key={market.code}
            market={market}
            index={rankOffset + index}
            count={rankingMarkets.length}
            leader={market.performanceIndex === maxPerformanceIndex}
            maxPerformanceIndex={maxPerformanceIndex}
            currencySymbol={currencySymbol}
          />
        ))}
      </Box>
    </Box>
  );
}
